/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import classnames from 'classnames';
import * as monaco from 'monaco-editor';

export interface CodeEditorProp {
  berforeRender?: React.ReactNode;
  afterRender?: React.ReactNode;
  value?: string;
  language?: string;
  className?: string;
  height?: number | string;
  readOnly?: boolean;
  onChange?: (value?: string) => void;
  onCompleted?: (value?: string) => boolean;
}

const codeEditorContainerId = 'ET_codeEditorContainerId';

export function CodeEditor({
  berforeRender,
  afterRender,
  value,
  className,
  language = 'javascript',
  height = '300px',
  readOnly = false,
  onChange,
  onCompleted,
}: CodeEditorProp) {
  const codeEditorRef = React.useRef<HTMLDivElement>(null);
  const [editor, setEditor] = React.useState<monaco.editor.IStandaloneCodeEditor>();
  const [oldValue, setOldValue] = useState('');

  React.useEffect(() => {
    if (!codeEditorRef.current) return;

    const editor = monaco.editor.create(codeEditorRef.current, {
      value: '',
      language,
      theme: 'vs-dark',
      readOnly,
      minimap: {
        enabled: false,
      },
      tabSize: 2,
      folding: false,
    });

    editor.layout();

    setEditor(editor);

    return () => {
      editor.dispose();
    };
  }, [language, readOnly]);

  React.useEffect(() => {
    if (!editor) return;

    const model = editor.getModel();
    if (!model) return;

    const range = model.getFullModelRange();
    editor.pushUndoStop();
    model.pushEditOperations(
      [],
      [
        {
          range,
          text: value || '',
        },
      ],
      () => null,
    );
    setOldValue(value || '');
    editor.popUndoStop();
  }, [editor, value]);

  React.useEffect(() => {
    if (!editor) return;
    editor.onDidChangeModelContent(() => {
      const changedValue = editor.getValue();
      !readOnly && onChange?.(changedValue);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, readOnly]);

  return (
    <div
      className={classnames(
        'mt-1 w-full flex flex-col gap-y-1 rounded border border-borderColorMain bg-FFFFFF0A relative',
        'code-editor',
        className,
      )}
    >
      {berforeRender}
      <div
        ref={codeEditorRef}
        id={codeEditorContainerId}
        style={{
          height,
        }}
        onBlur={() => {
          if (!editor) return;
          const changedValue = editor.getValue();
          if (!readOnly) {
            const passed = onCompleted?.(changedValue);
            if (passed) {
              setOldValue(changedValue);
            } else {
              if (!editor) return;

              const model = editor.getModel();
              if (!model) return;
              const range = model.getFullModelRange();
              editor.pushUndoStop();
              model.pushEditOperations(
                [],
                [
                  {
                    range,
                    text: oldValue || '',
                  },
                ],
                () => null,
              );
              editor.popUndoStop();
            }
          }
        }}
      />
      {afterRender}
    </div>
  );
}
